
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ExternalLink, WifiOff, AlertCircle } from 'lucide-react';
import { Payment } from '@/services/payment/types';  // Use correct import path
import { useFamilyPayments } from '@/hooks/useFamilyPayments';
import { useOnlineStatus } from '@/utils/connectionUtils';
import { Skeleton } from '@/components/ui/skeleton-loader';
import { useConnectionQuality } from '@/hooks/useConnectionQuality';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface FamilyPaymentHistoryProps {
  familyId: string;
}

const FamilyPaymentHistory: React.FC<FamilyPaymentHistoryProps> = ({ familyId }) => {
  const { data: payments, isLoading, error, refetch } = useFamilyPayments(familyId);
  const isOnline = useOnlineStatus();
  const { isSlowConnection } = useConnectionQuality();
  
  // Show offline indicator when applicable
  const isOfflineWithoutCache = !isOnline && (!payments || payments.length === 0);
  
  if (isLoading) {
    return (
      <Card className="backdrop-blur-sm border border-gray-100/40 bg-white/80 shadow-sm">
        <CardHeader>
          <CardTitle>Recent Payments</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {Array.from({ length: isSlowConnection ? 2 : 4 }).map((_, index) => (
            <div key={index} className="flex items-center justify-between">
              <div>
                <Skeleton className="h-5 w-20 mb-1" />
                <Skeleton className="h-4 w-24" />
              </div>
              <Skeleton className="h-8 w-28" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }
  
  if (error) {
    return (
      <Card className="backdrop-blur-sm border border-red-100 bg-white/80 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            Payment History Error
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>
              {error instanceof Error ? error.message : 'Error loading payment history'}
            </AlertDescription>
          </Alert>
          <div className="flex justify-end">
            <Button 
              variant="secondary" 
              size="sm" 
              onClick={() => refetch()}
              className="mt-2"
            >
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (!payments || payments.length === 0) {
    return (
      <Card className="backdrop-blur-sm border border-gray-100/40 bg-white/80 shadow-sm">
        <CardHeader>
          <CardTitle>Recent Payments</CardTitle>
        </CardHeader>
        <CardContent>
          {isOfflineWithoutCache ? (
            <Alert className="bg-amber-50 border-amber-200">
              <WifiOff className="h-4 w-4 text-amber-500" />
              <AlertDescription className="text-amber-700">
                You're offline. Connect to view payment history.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="text-muted-foreground p-6 text-center bg-gray-50/50 backdrop-blur-sm rounded-lg border border-gray-100/60">
              No payments yet
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="backdrop-blur-sm border border-gray-100/40 bg-white/80 shadow-sm hover:shadow-md transition-all duration-300">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="bg-gradient-to-r from-hivebubble-slate to-hivebubble-slate/70 bg-clip-text text-transparent">Recent Payments</span>
          {!isOnline && payments.length > 0 && (
            <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full">Offline data</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {payments.map((payment, index) => (
          <motion.div
            key={payment.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05, duration: 0.3 }}
            className="flex items-center justify-between text-sm p-2 hover:bg-gray-50/80 rounded-md transition-colors"
          >
            <div>
              <p className="font-semibold text-hivebubble-slate">${((payment.amount_cents || 0) / 100).toFixed(2)}</p>
              <p className="text-muted-foreground">{payment.paid_at ? new Date(payment.paid_at).toLocaleDateString() : 'No date'}</p>
            </div>
            {payment.receipt_url && isOnline && (
              <a
                href={payment.receipt_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-rosterly-blue hover:text-rosterly-purple flex items-center gap-1 transition-colors"
              >
                View Receipt
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
            {payment.receipt_url && !isOnline && (
              <span className="text-muted-foreground flex items-center gap-1">
                Receipt Unavailable Offline
              </span>
            )}
          </motion.div>
        ))}
      </CardContent>
    </Card>
  );
};

export default React.memo(FamilyPaymentHistory);
